/**
 * Ring buffer for asteroid sync correction diagnostics.
 *
 * Module-level singleton (same pattern as FrameTimingBuffer).
 * Pre-allocated array of AsteroidSyncEvent objects overwritten in-place
 * (zero allocation per event in recordAsteroidSync).
 *
 * Used to diagnose asteroid drift and collision event corrections at 20Hz sync rate.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AsteroidCorrection {
  index: number;
  clientX: number;
  clientY: number;
  serverX: number;
  serverY: number;
  clientVx: number;
  clientVy: number;
  serverVx: number;
  serverVy: number;
  positionDelta: number;
  velocityDelta: number;
  wasSnap: boolean;
}

export interface AsteroidSyncEvent {
  timestamp: number;
  serverTick: number;
  asteroidCount: number;
  corrections: AsteroidCorrection[];
  maxPositionDelta: number;
  snapCount: number;
  lerpCount: number;
}

export interface AsteroidSyncStats {
  eventCount: number;
  avgMaxPositionDelta: number;
  peakPositionDelta: number;
  avgSnapCount: number;
  avgLerpCount: number;
  lastEventTimestamp: number;
  lastMaxPositionDelta: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const ASTEROID_SYNC_BUFFER_SIZE = 50;

// ---------------------------------------------------------------------------
// Module state (singleton)
// ---------------------------------------------------------------------------

let buffer: AsteroidSyncEvent[] = [];
let writeIndex = 0;
let eventCount = 0;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Pre-allocate the ring buffer and reset counters.
 * Call once at game loop start.
 */
export function initAsteroidSyncLog(): void {
  buffer = new Array<AsteroidSyncEvent>(ASTEROID_SYNC_BUFFER_SIZE);
  for (let i = 0; i < ASTEROID_SYNC_BUFFER_SIZE; i++) {
    buffer[i] = {
      timestamp: 0,
      serverTick: 0,
      asteroidCount: 0,
      corrections: [],
      maxPositionDelta: 0,
      snapCount: 0,
      lerpCount: 0,
    };
  }
  writeIndex = 0;
  eventCount = 0;
}

/**
 * Record one asteroid sync event.
 * The corrections array is replaced each time (unavoidable — variable-length per-asteroid data).
 * The ring buffer itself is pre-allocated; only the corrections array allocates.
 */
export function recordAsteroidSync(event: AsteroidSyncEvent): void {
  if (buffer.length === 0) return;

  const slot = buffer[writeIndex];
  slot.timestamp = event.timestamp;
  slot.serverTick = event.serverTick;
  slot.asteroidCount = event.asteroidCount;
  slot.corrections = event.corrections;
  slot.maxPositionDelta = event.maxPositionDelta;
  slot.snapCount = event.snapCount;
  slot.lerpCount = event.lerpCount;

  eventCount++;
  writeIndex = (writeIndex + 1) % ASTEROID_SYNC_BUFFER_SIZE;
}

/**
 * Return chronological array of all recorded events.
 * Allocates — only call at export time.
 */
export function getAsteroidSyncLog(): AsteroidSyncEvent[] {
  const validCount = Math.min(eventCount, ASTEROID_SYNC_BUFFER_SIZE);
  if (validCount === 0) return [];

  const result: AsteroidSyncEvent[] = [];
  if (eventCount <= ASTEROID_SYNC_BUFFER_SIZE) {
    for (let i = 0; i < validCount; i++) {
      result.push({ ...buffer[i], corrections: [...buffer[i].corrections] });
    }
  } else {
    for (let i = 0; i < ASTEROID_SYNC_BUFFER_SIZE; i++) {
      const idx = (writeIndex + i) % ASTEROID_SYNC_BUFFER_SIZE;
      result.push({ ...buffer[idx], corrections: [...buffer[idx].corrections] });
    }
  }
  return result;
}

/**
 * Return summary stats for DebugPanel display.
 * Cheap — iterates buffer but does not deep-copy corrections.
 */
export function getAsteroidSyncStats(): AsteroidSyncStats {
  const validCount = Math.min(eventCount, ASTEROID_SYNC_BUFFER_SIZE);
  if (validCount === 0) {
    return {
      eventCount: 0,
      avgMaxPositionDelta: 0,
      peakPositionDelta: 0,
      avgSnapCount: 0,
      avgLerpCount: 0,
      lastEventTimestamp: 0,
      lastMaxPositionDelta: 0,
    };
  }

  let sumMaxDelta = 0;
  let peakDelta = 0;
  let sumSnap = 0;
  let sumLerp = 0;

  const startIdx = eventCount <= ASTEROID_SYNC_BUFFER_SIZE ? 0 : writeIndex;
  for (let i = 0; i < validCount; i++) {
    const slot = buffer[(startIdx + i) % ASTEROID_SYNC_BUFFER_SIZE];
    sumMaxDelta += slot.maxPositionDelta;
    if (slot.maxPositionDelta > peakDelta) peakDelta = slot.maxPositionDelta;
    sumSnap += slot.snapCount;
    sumLerp += slot.lerpCount;
  }

  const lastIdx = (writeIndex - 1 + ASTEROID_SYNC_BUFFER_SIZE) % ASTEROID_SYNC_BUFFER_SIZE;
  const last = buffer[lastIdx];

  return {
    eventCount,
    avgMaxPositionDelta: sumMaxDelta / validCount,
    peakPositionDelta: peakDelta,
    avgSnapCount: sumSnap / validCount,
    avgLerpCount: sumLerp / validCount,
    lastEventTimestamp: last.timestamp,
    lastMaxPositionDelta: last.maxPositionDelta,
  };
}
